import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { resolveProfileImage } from "@/lib/api";

const IDENTITY_CACHE_KEY = "hassein.pwa.identity";

/**
 * Dynamically updates the PWA install identity (name + icon) based on the
 * currently-known user. Works in three tiers:
 *
 *   1. If a logged-in `user` from AuthContext is available -> apply immediately.
 *   2. Otherwise, hydrate from localStorage cache (last known user) so that even
 *      on the login screen (or before the auth session is restored) the
 *      Add-to-Home-Screen sheet already shows the right name + photo.
 *   3. If only a remembered email is present, fetch /auth/quick-login-info to
 *      learn the display name and profile picture, and apply.
 *
 * - iOS: <meta apple-mobile-web-app-title> + <link rel="apple-touch-icon">
 * - Android/Chrome: dynamic manifest via Blob URL replacement.
 * - Also updates document.title and favicon.
 */
export default function PwaIdentity() {
  const { user } = useAuth();
  const [prefetched, setPrefetched] = useState(null);

  // Prefetch identity from cache / quick-login-info when no user yet.
  useEffect(() => {
    if (user) return;
    try {
      const cached = localStorage.getItem(IDENTITY_CACHE_KEY);
      if (cached) {
        setPrefetched(JSON.parse(cached));
      }
    } catch { /* ignore */ }

    const email = (() => {
      try { return localStorage.getItem("hassein.remembered.email") || ""; }
      catch { return ""; }
    })();
    if (!email) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/auth/quick-login-info?email=${encodeURIComponent(email)}`);
        if (cancelled || !res.data?.exists) return;
        const info = {
          full_name: res.data.full_name,
          email,
          profile_image: res.data.profile_image,
        };
        setPrefetched(info);
        try { localStorage.setItem(IDENTITY_CACHE_KEY, JSON.stringify(info)); } catch { /* noop */ }
      } catch { /* offline / not-found -> stay with cache */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    const source = user || prefetched;

    // If we have NO user and no cached identity — reset the PWA identity to
    // the generic defaults so a new visitor / signed-out session never inherits
    // the previous user's name or photo when adding to home screen.
    if (!source || typeof source !== "object") {
      document.title = "ניהול פיננסי";
      const tm = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (tm) tm.setAttribute("content", "ניהול פיננסי");
      document.querySelectorAll('link[data-pwa-identity="true"]').forEach(n => n.remove());
      // Point manifest back to the static generic file (already served from public/)
      const manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink) {
        const old = manifestLink.getAttribute("href");
        manifestLink.setAttribute("href", "/manifest.json");
        if (old && old.startsWith("blob:")) URL.revokeObjectURL(old);
      }
      return;
    }

    const fullName = source.full_name || source.name || "";
    const email = source.email || "";
    const displayName = fullName || email || "ניהול פיננסי";

    const photo = resolveProfileImage(source.profile_image || source.profile_photo || "");

    // Cache identity for next visit so meta tags are correct even before the
    // network request completes (or on offline installs).
    if (user) {
      try {
        localStorage.setItem(IDENTITY_CACHE_KEY, JSON.stringify({
          full_name: fullName, email, profile_image: source.profile_image || source.profile_photo || "",
        }));
      } catch { /* noop */ }
    }

    // 1. Document title (browser tab)
    document.title = displayName;

    // 2. iOS apple-mobile-web-app-title
    let titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!titleMeta) {
      titleMeta = document.createElement("meta");
      titleMeta.setAttribute("name", "apple-mobile-web-app-title");
      document.head.appendChild(titleMeta);
    }
    titleMeta.setAttribute("content", displayName);

    // 3. iOS apple-touch-icon + favicon (multiple sizes for reliable iOS pickup)
    if (photo) {
      document.querySelectorAll('link[data-pwa-identity="true"]').forEach(n => n.remove());
      const rels = [
        { rel: "apple-touch-icon", sizes: "180x180" },
        { rel: "apple-touch-icon", sizes: "192x192" },
        { rel: "apple-touch-icon-precomposed", sizes: "180x180" },
        { rel: "icon", sizes: "192x192" },
        { rel: "shortcut icon", sizes: undefined },
      ];
      rels.forEach(({ rel, sizes }) => {
        const link = document.createElement("link");
        link.setAttribute("rel", rel);
        link.setAttribute("href", photo);
        if (sizes) link.setAttribute("sizes", sizes);
        link.setAttribute("data-pwa-identity", "true");
        document.head.appendChild(link);
      });
    }

    // 4. Dynamic Android/Chrome manifest via Blob URL
    const manifest = {
      short_name: displayName,
      name: displayName,
      description: "ניהול פיננסי אישי",
      icons: photo ? [
        { src: photo, sizes: "192x192", type: "image/png", purpose: "any maskable" },
        { src: photo, sizes: "512x512", type: "image/png", purpose: "any maskable" },
      ] : [],
      start_url: "/",
      display: "standalone",
      orientation: "portrait",
      theme_color: "#2C5E4E",
      background_color: "#F9FAF9",
      lang: "he",
      dir: "rtl",
    };
    const blob = new Blob([JSON.stringify(manifest)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    let manifestLink = document.querySelector('link[rel="manifest"]');
    if (!manifestLink) {
      manifestLink = document.createElement("link");
      manifestLink.setAttribute("rel", "manifest");
      document.head.appendChild(manifestLink);
    }
    const oldHref = manifestLink.getAttribute("href");
    manifestLink.setAttribute("href", url);
    if (oldHref && oldHref.startsWith("blob:")) URL.revokeObjectURL(oldHref);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [user, prefetched]);

  return null;
}
